/**
 * Clubs Screen
 *
 * Listagem e gerenciamento de clubes
 */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Eye,
	Trash2,
	MapPin,
	Users,
	Calendar,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { ConfirmModal } from '../molecules/Modal'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { useClubs } from '../../core/admin.hooks'
import { clubsApi } from '../../core/admin.api'
import { ADMIN_ROUTES, BRAZILIAN_STATES } from '../../core/admin.constants'
import { formatDate } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function ClubsScreen() {
	const navigate = useNavigate()
	const [filters, setFilters] = useState({})
	const [selectedClub, setSelectedClub] = useState(null)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
		refetch,
	} = useClubs(filters)

	const clubs = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Opções de estado para filtro
	const stateOptions = [
		{ value: '', label: 'Todos os estados' },
		...BRAZILIAN_STATES,
	]

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'state',
			label: 'Estado',
			type: 'select',
			options: stateOptions,
		},
	]

	// Colunas da tabela
	const columns = [
		{
			key: 'club',
			label: 'Clube',
			render: (row) => (
				<div className="flex items-center gap-2 md:gap-3 min-w-0">
					<Avatar
						src={row.image}
						name={row.name}
						size="sm"
						className="flex-shrink-0"
					/>
					<div className="min-w-0 flex-1">
						<p className="font-medium text-text text-sm truncate">{row.name}</p>
						{row.address && (
							<p className="text-xs text-text-body truncate">
								<MapPin className="w-3 h-3 inline mr-1" />
								{row.address.city}, {row.address.state}
							</p>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'owner',
			label: 'Dono',
			render: (row) => (
				<div className="flex items-center gap-2 min-w-0">
					<Avatar
						src={row.owner?.avatar}
						name={row.owner?.name}
						size="sm"
						className="flex-shrink-0"
					/>
					<span className="text-text-body text-sm truncate">
						{row.owner?.name || 'N/A'}
					</span>
				</div>
			),
		},
		{
			key: 'members',
			label: 'Membros',
			render: (row) => (
				<div className="flex items-center gap-1 text-text-body text-sm">
					<Users className="w-4 h-4" />
					<span>{row.playersCount || 0}</span>
				</div>
			),
		},
		{
			key: 'matches',
			label: 'Partidas',
			render: (row) => (
				<div className="flex items-center gap-1 text-text-body text-sm">
					<Calendar className="w-4 h-4" />
					<span>{row.matchesCount || 0}</span>
				</div>
			),
		},
		{
			key: 'createdAt',
			label: 'Criado em',
			render: (row) => (
				<span className="text-text-muted text-xs">
					{formatDate(row.createdAt, 'dd/MM/yy')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => navigate(`${ADMIN_ROUTES.CLUBS}/${row._id}`),
		},
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedClub(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedClub) return

		setActionLoading(true)
		try {
			await clubsApi.delete(selectedClub._id)
			toast.success('Clube excluído com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir clube')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Clubes" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Clubes"
				subtitle={`${total} clubes cadastrados`}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por nome, cidade..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={clubs}
					loading={loading}
					emptyMessage="Nenhum clube encontrado"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
					mobileColumns={['club']}
				/>
			</div>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Clube"
				message={`Tem certeza que deseja excluir o clube "${selectedClub?.name}"? Todos os dados relacionados (partidas, membros, etc.) serão removidos. Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
